import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha } from '@mui/material/styles';
import MovePill from './MovePill';
import { getModelLabel, getProviderLabel } from '../../services/llmCredentials';
import { DeepDiveApiError, runDeepDive } from '../../services/newsService';

/**
 * Layer 2 Deep Dive dialog (W6).
 *
 * Opens on the Deep Dive button (per-security). Runs `runDeepDive` with the
 * caller-supplied BYOK credentials, renders the resulting markdown brief
 * inline, and drives regenerate + retry + error-mapping UX.
 *
 * Contract with the caller (`NewsCell`):
 *   - `credentials`: `{provider, model, api_key}` from `loadLlmCredentials()`.
 *     `null` is a bug on the caller's side — the click journey is supposed to
 *     redirect through `LlmSettingsDialog` first — but the dialog degrades
 *     gracefully (renders an inline "no credentials" alert, does not fire).
 *   - `onRequireSettings(message)`: called when a 401 (or 400 unknown
 *     provider/model) comes back so the parent can bounce the user into
 *     `LlmSettingsDialog`. The mapped error copy is passed as the argument,
 *     because the parent closes this dialog on the way and the snackbar below
 *     goes with it — the receiving dialog has to be able to say why.
 *
 * Error mapping (from `DeepDiveApiError.status`):
 *   - 401 -> "Your API key was rejected by the provider. Please update it in
 *           settings." Auto-reopens LlmSettingsDialog, which renders the same
 *           copy as a persistent warning; the snackbar here is the transient
 *           echo, not the only channel.
 *   - 403 -> snackbar "This security isn't on your watchlist." (defensive —
 *           this shouldn't happen from the UI).
 *   - 400 -> snackbar with the server message + reopen LlmSettingsDialog
 *           (unknown provider/model — most likely a stored value drifted).
 *   - 429 -> snackbar "Rate limit reached - try again shortly." No auto-retry.
 *   - 504 -> snackbar "Deep dive timed out. Retry?" with retry action.
 *   - other/network -> snackbar + inline Retry button in the dialog body.
 *
 * Deep-dive calls take 30-90s. The dialog opens immediately with a spinner +
 * rotating status text so users see the app is still working, not stuck.
 */

// Rotating status copy while the request is in flight. One string swap every
// ~STATUS_ROTATION_MS makes a 30-90s wait feel intentional rather than
// broken. Kept short and factually accurate about what the backend is doing.
export const LOADING_STATUS_MESSAGES = [
    'Searching recent coverage…',
    'Reading provider filings…',
    'Drafting brief…',
];
const STATUS_ROTATION_MS = 8000;

function formatCreatedAt(raw) {
    if (!raw) return '';
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function providerLabel(provider) {
    return getProviderLabel(provider) || (provider || 'Unknown');
}

function modelLabel(model) {
    return getModelLabel(model) || (model || '');
}

function buildProviderChipLabel(provider, model) {
    const p = providerLabel(provider);
    const m = modelLabel(model);
    if (m) return `${p} — ${m}`;
    return p;
}

/**
 * Map a `DeepDiveApiError` (or generic Error) to the UI intent needed to
 * present it. Returns `{message, requiresSettings, allowRetry}`.
 */
export function mapDeepDiveError(err) {
    const isTyped = err instanceof DeepDiveApiError;
    const status = isTyped ? err.status : null;
    const detail = (isTyped && err.detail) ? err.detail : '';
    switch (status) {
        case 401:
            return {
                message: 'Your API key was rejected by the provider. Please update it in settings.',
                requiresSettings: true,
                allowRetry: false,
            };
        case 403:
            return {
                message: "This security isn't on your watchlist.",
                requiresSettings: false,
                allowRetry: false,
            };
        case 400:
            return {
                message: detail || 'Deep dive rejected the request. Please check your settings.',
                requiresSettings: true,
                allowRetry: false,
            };
        case 429:
            return {
                message: detail || 'Rate limit reached — try again shortly.',
                requiresSettings: false,
                allowRetry: false,
            };
        case 504:
            return {
                message: 'Deep dive timed out. Retry?',
                requiresSettings: false,
                allowRetry: true,
            };
        default:
            return {
                message: 'Deep dive failed. Please retry.',
                requiresSettings: false,
                allowRetry: true,
            };
    }
}

/**
 * Confirm dialog for the Regenerate action. Simple wrapper around MUI Dialog.
 */
function RegenerateConfirmDialog({ open, onCancel, onConfirm }) {
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            data-testid="deep-dive-regenerate-confirm"
            PaperProps={{ sx: { borderRadius: 2, backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, px: 2.5, pt: 2, pb: 0.5 }}>
                Regenerate deep dive?
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pt: 1, pb: 1.5 }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.5 }}>
                    This will spend your provider credits. Continue?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
                <Button
                    onClick={onCancel}
                    size="small"
                    color="inherit"
                    sx={{ textTransform: 'none' }}
                    data-testid="deep-dive-regenerate-cancel"
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    data-testid="deep-dive-regenerate-confirm-yes"
                >
                    Regenerate
                </Button>
            </DialogActions>
        </Dialog>
    );
}

RegenerateConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};

export default function DeepDiveDialog({
    open,
    onClose,
    item,
    moverThresholdPct,
    credentials,
    focusAreas = null,
    onRequireSettings = null,
}) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // { content_md, citations, cached, ... }
    const [errorState, setErrorState] = useState(null); // { message, requiresSettings, allowRetry }
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
    const [statusIdx, setStatusIdx] = useState(0);

    // Guard against duplicate submissions racing each other in the same
    // dialog session. `activeRequestRef` tracks the in-flight promise so
    // we can ignore late-returning ones after a force-close / retry.
    const activeRequestRef = useRef(0);

    const provider = credentials?.provider || item?.deepDiveProvider || '';
    const model = credentials?.model || '';

    const runOnce = useCallback(async (forceRefresh) => {
        if (!credentials || !credentials.api_key) {
            setErrorState({
                message: 'Configure your provider API key first.',
                requiresSettings: true,
                allowRetry: false,
            });
            setLoading(false);
            return;
        }
        if (!item?.cognive_id) {
            setErrorState({
                message: 'This security is missing an identity — cannot run deep dive.',
                requiresSettings: false,
                allowRetry: false,
            });
            setLoading(false);
            return;
        }
        const requestId = ++activeRequestRef.current;
        setLoading(true);
        setErrorState(null);
        setResult(null);
        try {
            const payload = await runDeepDive({
                cognive_id: item.cognive_id,
                provider: credentials.provider,
                model: credentials.model,
                api_key: credentials.api_key,
                force_refresh: Boolean(forceRefresh),
                focus_areas: Array.isArray(focusAreas) && focusAreas.length > 0
                    ? focusAreas
                    : ['all'],
            });
            if (requestId !== activeRequestRef.current) return; // stale
            setResult(payload);
        } catch (err) {
            if (requestId !== activeRequestRef.current) return; // stale
            const mapped = mapDeepDiveError(err);
            setErrorState(mapped);
            setSnackbar({ open: true, message: mapped.message, severity: 'error' });
            if (mapped.requiresSettings && typeof onRequireSettings === 'function') {
                // Hand the copy over: this dialog is about to be closed, so
                // its own error state and snackbar stop being a channel the
                // user can actually read.
                onRequireSettings(mapped.message);
            }
        } finally {
            if (requestId === activeRequestRef.current) {
                setLoading(false);
            }
        }
    }, [credentials, item, focusAreas, onRequireSettings]);

    // Fire the initial request when the dialog transitions closed -> open.
    // Bump activeRequestRef on close so an in-flight response for the
    // previous session is dropped when the user re-opens.
    const openRef = useRef(false);
    useEffect(() => {
        if (!open) {
            openRef.current = false;
            // Invalidate any in-flight request when the dialog closes so its
            // late-arriving state update no-ops (see requestId check above).
            activeRequestRef.current += 1;
            return;
        }
        if (openRef.current) return;
        openRef.current = true;
        void runOnce(false);
    }, [open, runOnce]);

    // Rotate the loading-status copy on an interval so the wait feels active.
    useEffect(() => {
        if (!loading) {
            setStatusIdx(0);
            return undefined;
        }
        const id = window.setInterval(() => {
            setStatusIdx((prev) => (prev + 1) % LOADING_STATUS_MESSAGES.length);
        }, STATUS_ROTATION_MS);
        return () => window.clearInterval(id);
    }, [loading]);

    const handleClose = useCallback(() => {
        if (typeof onClose === 'function') onClose();
    }, [onClose]);

    const handleRequestRegenerate = useCallback(() => {
        if (loading) return;
        setConfirmRegenerateOpen(true);
    }, [loading]);

    const handleConfirmRegenerate = useCallback(() => {
        setConfirmRegenerateOpen(false);
        void runOnce(true);
    }, [runOnce]);

    const handleCancelRegenerate = useCallback(() => {
        setConfirmRegenerateOpen(false);
    }, []);

    const handleRetry = useCallback(() => {
        if (loading) return;
        void runOnce(false);
    }, [loading, runOnce]);

    const closeSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    const rawReturnPct = item?.return_pct;
    const returnPct = Number(rawReturnPct);
    const hasReturnPct = rawReturnPct !== null
        && rawReturnPct !== undefined
        && Number.isFinite(returnPct);

    const displayName = String(item?.display_name || item?.sedol || 'Security').trim();
    const providerChipLabel = useMemo(
        () => buildProviderChipLabel(provider, model),
        [provider, model],
    );

    const cached = Boolean(result?.cached);
    const cachedTimestamp = formatCreatedAt(result?.created_at);
    const citations = Array.isArray(result?.citations) ? result.citations : [];

    return (
        <>
            <Dialog
                open={Boolean(open)}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                data-testid="deep-dive-dialog"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        backgroundImage: 'none',
                        minHeight: 360,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexWrap: 'wrap',
                        px: 2.5,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{ fontSize: '1rem', fontWeight: 700, flex: 1, minWidth: 0 }}
                        data-testid="deep-dive-title"
                    >
                        {displayName}
                    </Typography>
                    {hasReturnPct && (
                        <MovePill
                            pct={returnPct}
                            threshold={Number.isFinite(Number(moverThresholdPct))
                                ? Number(moverThresholdPct)
                                : 2.0}
                            testId="deep-dive-move-pill"
                        />
                    )}
                    {provider && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={providerChipLabel}
                            data-testid="deep-dive-provider-chip"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                    )}
                    <IconButton
                        aria-label="Close deep dive"
                        onClick={handleClose}
                        size="small"
                        data-testid="deep-dive-close-button"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', minHeight: 260 }}
                >
                    {loading && (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                py: 4,
                            }}
                            data-testid="deep-dive-loading"
                        >
                            <CircularProgress size={32} />
                            <Typography
                                sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
                                data-testid="deep-dive-status"
                            >
                                {LOADING_STATUS_MESSAGES[statusIdx]}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                                This usually takes 30-90 seconds.
                            </Typography>
                        </Box>
                    )}

                    {!loading && errorState && (
                        <Box sx={{ py: 2 }} data-testid="deep-dive-error">
                            <Alert
                                severity="error"
                                action={errorState.allowRetry ? (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={handleRetry}
                                        data-testid="deep-dive-retry-button"
                                    >
                                        Retry
                                    </Button>
                                ) : undefined}
                            >
                                {errorState.message}
                            </Alert>
                        </Box>
                    )}

                    {!loading && !errorState && result && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {cached && (
                                <Box
                                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                                    data-testid="deep-dive-cached-row"
                                >
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={cachedTimestamp
                                            ? `Generated earlier today, ${cachedTimestamp}`
                                            : 'Generated earlier today'}
                                        data-testid="deep-dive-cached-chip"
                                        sx={{ fontSize: '0.7rem', height: 22 }}
                                    />
                                    <Tooltip title="Re-run this deep dive against your provider (uses your credits).">
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<RefreshIcon fontSize="small" />}
                                            onClick={handleRequestRegenerate}
                                            disabled={loading}
                                            data-testid="deep-dive-regenerate-button"
                                            sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                                        >
                                            Regenerate
                                        </Button>
                                    </Tooltip>
                                </Box>
                            )}

                            <Box
                                sx={{
                                    fontSize: '0.88rem',
                                    lineHeight: 1.55,
                                    color: 'text.primary',
                                    '& h2': { fontSize: '0.95rem', fontWeight: 700, mt: 1.75, mb: 0.75 },
                                    '& h3': { fontSize: '0.9rem', fontWeight: 700, mt: 1.5, mb: 0.5 },
                                    '& p': { my: 0.75 },
                                    '& a': { color: 'primary.main', textDecoration: 'underline' },
                                    '& ul, & ol': { pl: 3, my: 0.75 },
                                    '& li': { my: 0.25 },
                                    '& code': {
                                        px: 0.5,
                                        borderRadius: 0.5,
                                        backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                        fontSize: '0.8rem',
                                    },
                                }}
                                data-testid="deep-dive-markdown"
                            >
                                <ReactMarkdown
                                    // No HTML pass-through (react-markdown v9 default) and
                                    // no plugins — safer + smaller bundle. Links are
                                    // forced to open in a new tab.
                                    components={{
                                        a: ({ href, children, ...rest }) => (
                                            <Link
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                {...rest}
                                            >
                                                {children}
                                            </Link>
                                        ),
                                    }}
                                >
                                    {String(result.content_md || '')}
                                </ReactMarkdown>
                            </Box>

                            {citations.length > 0 && (
                                <Box
                                    sx={{ mt: 1, pt: 1, borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}` }}
                                    data-testid="deep-dive-citations"
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}
                                    >
                                        Sources
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, my: 0.75 }}>
                                        {citations.map((c, idx) => (
                                            <Box
                                                component="li"
                                                key={`${c?.url || 'citation'}-${idx}`}
                                                sx={{ fontSize: '0.78rem', lineHeight: 1.45 }}
                                            >
                                                {c?.url ? (
                                                    <Link
                                                        href={c.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {c.title || c.url}
                                                    </Link>
                                                ) : (
                                                    <span>{c?.title || 'Untitled source'}</span>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2.5, py: 1.25 }}>
                    <Button
                        onClick={handleClose}
                        size="small"
                        color="inherit"
                        sx={{ textTransform: 'none' }}
                        data-testid="deep-dive-actions-close"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <RegenerateConfirmDialog
                open={confirmRegenerateOpen}
                onCancel={handleCancelRegenerate}
                onConfirm={handleConfirmRegenerate}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                data-testid="deep-dive-snackbar"
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

DeepDiveDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    item: PropTypes.shape({
        cognive_id: PropTypes.string,
        sedol: PropTypes.string,
        display_name: PropTypes.string,
        return_pct: PropTypes.number,
        is_big_mover: PropTypes.bool,
    }),
    moverThresholdPct: PropTypes.number,
    credentials: PropTypes.shape({
        provider: PropTypes.string,
        model: PropTypes.string,
        api_key: PropTypes.string,
    }),
    onRequireSettings: PropTypes.func,
};
